/** SYSTEM HEALTH — tab panels (Application · Database · API failures · Jobs · Integrations · Logs · History). */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { KV, StatusPill, Table, fmtSince, fmtUptime } from "./systemHealthParts";

function useFetch(url, deps = []) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { setBusy(true); try { const { data } = await api.get(url); setData(data); setErr(null); } catch (e) { setErr(e.response?.data?.detail || "Failed to load"); } finally { setBusy(false); } };
  useEffect(() => { load(); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, err, busy, reload: load };
}

const Card = ({ title, children, right, testId }) => (
  <div className="os-card p-4 space-y-2" data-testid={testId}>
    <div className="flex items-center gap-2"><b className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)]">{title}</b><span className="ml-auto">{right}</span></div>
    {children}
  </div>
);

export function ApplicationTab({ comp }) {
  const m = comp?.metrics || {};
  const w = m.window_15m || {}, w60 = m.window_60m || {};
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="sh-tab-application">
      <Card title="Process">
        <KV k="Uptime" v={fmtUptime(m.uptime_seconds)} testId="sh-app-uptime" />
        <KV k="Started" v={m.started_at ? new Date(m.started_at).toLocaleString() : "—"} />
        <KV k="Memory (RSS)" v={m.rss_mb != null ? `${m.rss_mb} MB` : "—"} />
        <KV k="Load (1m)" v={m.load_1m != null ? `${m.load_1m} / ${m.cpus} CPU` : "—"} />
        <KV k="Disk used" v={`${m.disk_used_pct ?? "—"}% · ${m.disk_free_gb ?? "—"} GB free`} />
      </Card>
      <Card title="Traffic · last 15 min">
        <KV k="Requests" v={`${w.requests ?? 0} (${w.rpm ?? 0}/min)`} testId="sh-app-req15" />
        <KV k="5xx errors" v={w.errors_5xx ?? 0} />
        <KV k="4xx responses" v={w.errors_4xx ?? 0} />
        <KV k="Error rate" v={`${w.error_rate ?? 0}%`} />
        <KV k="Latency avg / p95 / max" v={`${w.avg_ms ?? 0} / ${w.p95_ms ?? 0} / ${w.max_ms ?? 0} ms`} />
      </Card>
      <Card title="Traffic · last 60 min">
        <KV k="Requests" v={`${w60.requests ?? 0} (${w60.rpm ?? 0}/min)`} />
        <KV k="5xx errors" v={w60.errors_5xx ?? 0} />
        <KV k="Error rate" v={`${w60.error_rate ?? 0}%`} />
        <KV k="p95 latency" v={`${w60.p95_ms ?? 0} ms`} />
        <KV k="Since start" v={`${m.requests_since_start ?? 0} req · ${m.errors_5xx_since_start ?? 0} × 5xx`} />
      </Card>
    </div>
  );
}

export function DatabaseTab() {
  const { data, err } = useFetch("/system-health/database");
  if (err) return <div className="text-[12px] text-[color:var(--st-critical)]">{err}</div>;
  if (!data) return <div className="os-skeleton h-40" />;
  const m = data.metrics || {};
  return (
    <div className="grid md:grid-cols-[320px_1fr] gap-4" data-testid="sh-tab-database">
      <Card title="MongoDB" right={<StatusPill status={data.status} testId="sh-db-status" />}>
        <KV k="Ping" v={m.ping_ms != null ? `${m.ping_ms} ms` : "—"} testId="sh-db-ping" />
        <KV k="Database" v={m.db_name} />
        <KV k="Collections" v={m.collections} />
        <KV k="Documents" v={m.objects?.toLocaleString?.()} />
        <KV k="Data / Storage / Index" v={`${m.data_mb ?? 0} / ${m.storage_mb ?? 0} / ${m.index_mb ?? 0} MB`} />
        {data.status === "failed" && <div className="text-[12px] text-[color:var(--st-critical)]">{data.detail}</div>}
      </Card>
      <Card title={`Largest collections · ${data.collection_count ?? 0} total`}>
        <Table testId="sh-db-collections" empty="No collections" rowKey={(r) => r.collection}
          cols={[{ k: "collection", h: "Collection", r: (r) => <span className="font-mono">{r.collection}</span> },
            { k: "count", h: "Documents", r: (r) => r.count.toLocaleString() },
            { k: "size_mb", h: "Data MB" }, { k: "storage_mb", h: "Storage MB" }]}
          rows={data.top_collections || []} />
      </Card>
    </div>
  );
}

export function ApiFailuresTab() {
  const [hours, setHours] = useState(24);
  const { data, err, reload, busy } = useFetch(`/system-health/api-failures?hours=${hours}&limit=200`, [hours]);
  const [open, setOpen] = useState(null);
  if (err) return <div className="text-[12px] text-[color:var(--st-critical)]">{err}</div>;
  return (
    <div className="space-y-4" data-testid="sh-tab-api">
      <div className="flex items-center gap-2 text-[12px]">
        {[1, 6, 24, 168].map((h) => <button key={h} onClick={() => setHours(h)} data-testid={`sh-api-hours-${h}`} className={`h-7 px-3 rounded-full border text-[11px] font-medium ${hours === h ? "bg-[color:var(--st-text)] text-white border-transparent" : "border-[color:var(--st-border)] bg-white"}`}>{h === 168 ? "7 d" : `${h} h`}</button>)}
        <button onClick={reload} className="ml-auto h-7 w-7 grid place-items-center rounded-md hover:bg-[color:var(--st-surface-alt)]" data-testid="sh-api-refresh"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /></button>
      </div>
      {!data ? <div className="os-skeleton h-40" /> : (
        <>
          <div className="grid md:grid-cols-[1fr_1.4fr] gap-4">
            <Card title="Top failing endpoints">
              <Table testId="sh-api-top" empty="No failing endpoints in this window" rowKey={(r) => r.method + r.path}
                cols={[{ k: "path", h: "Endpoint", r: (r) => <span className="font-mono text-[11px]"><b>{r.method}</b> {r.path}</span> },
                  { k: "count", h: "Count", r: (r) => <b className="text-[color:var(--st-critical)]">{r.count}</b> },
                  { k: "last_at", h: "Last", r: (r) => fmtSince(r.last_at) }]}
                rows={data.top_endpoints || []} />
            </Card>
            <Card title="Live window (in-process)">
              <div className="grid grid-cols-2 gap-x-6">
                <div><div className="text-[10px] uppercase text-[color:var(--st-text-subtle)] mb-1">15 min</div>{Object.entries(data.window?.["15m"] || {}).filter(([k]) => k !== "minutes").map(([k, v]) => <KV key={k} k={k.replace(/_/g, " ")} v={String(v)} />)}</div>
                <div><div className="text-[10px] uppercase text-[color:var(--st-text-subtle)] mb-1">60 min</div>{Object.entries(data.window?.["60m"] || {}).filter(([k]) => k !== "minutes").map(([k, v]) => <KV key={k} k={k.replace(/_/g, " ")} v={String(v)} />)}</div>
              </div>
            </Card>
          </div>
          <Card title={`Failures · ${data.count} in last ${hours === 168 ? "7 d" : `${hours} h`}`}>
            <Table testId="sh-api-failures" empty="No API failures recorded — 5xx responses and unhandled exceptions will appear here automatically" rowKey={(r) => r.id}
              cols={[{ k: "at", h: "When", r: (r) => <span title={r.at}>{fmtSince(r.at)}</span> },
                { k: "status", h: "Status", r: (r) => <span className="font-mono text-[color:var(--st-critical)] font-semibold">{r.status}</span> },
                { k: "path", h: "Endpoint", r: (r) => <span className="font-mono text-[11px]"><b>{r.method}</b> {r.path}</span> },
                { k: "ms", h: "ms" },
                { k: "error", h: "Error", r: (r) => (
                  <div>
                    <button onClick={() => setOpen(open === r.id ? null : r.id)} className="inline-flex items-center gap-1 text-left" data-testid={`sh-api-fail-${r.id}`}>{r.traceback ? (open === r.id ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />) : null}<span className="break-all">{r.error || "—"}</span></button>
                    {open === r.id && r.traceback && <pre className="mt-2 p-2 rounded bg-[#0B1730] text-[#E6EDF7] text-[10px] leading-relaxed overflow-x-auto max-h-64">{r.traceback}</pre>}
                  </div>) }]}
              rows={data.rows || []} />
          </Card>
        </>
      )}
    </div>
  );
}

export function JobsTab() {
  const { data, err, busy, reload } = useFetch("/system-health/jobs");
  const [runs, setRuns] = useState(null);
  const showRuns = async (id) => { try { const { data } = await api.get(`/system-health/jobs/${encodeURIComponent(id)}/runs`); setRuns(data); } catch { toast.error("Could not load runs"); } };
  if (err) return <div className="text-[12px] text-[color:var(--st-critical)]">{err}</div>;
  if (!data) return <div className="os-skeleton h-40" />;
  return (
    <div className="space-y-4" data-testid="sh-tab-jobs">
      <div className="flex items-center gap-2 text-[12px]"><StatusPill status={data.status} testId="sh-jobs-status" /><span className="text-[color:var(--st-text-muted)]">{data.detail}</span>
        <button onClick={reload} className="ml-auto h-7 w-7 grid place-items-center rounded-md hover:bg-[color:var(--st-surface-alt)]" data-testid="sh-jobs-refresh"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /></button></div>
      <Card title={`Scheduled jobs · ${data.rows.length}`}>
        <Table testId="sh-jobs-table" empty="No jobs configured" rowKey={(r) => r.id}
          cols={[{ k: "status", h: "Status", r: (r) => <StatusPill status={r.status} testId={`sh-job-${r.id}-status`} /> },
            { k: "id", h: "Job", r: (r) => <div><div className="font-semibold">{r.id}</div><div className="text-[11px] text-[color:var(--st-text-muted)] max-w-[420px]">{r.description}</div></div> },
            { k: "schedule", h: "Schedule", r: (r) => <span className="font-mono text-[11px]">{r.schedule}{r.kind === "platform_cron" ? ` · every ${r.interval_hours}h` : ""}</span> },
            { k: "last_run_at", h: "Last run", r: (r) => <span title={r.last_run_at || ""}>{fmtSince(r.last_run_at)}</span> },
            { k: "note", h: "Note" },
            { k: "x", h: "", r: (r) => r.kind === "platform_cron" && <button onClick={() => showRuns(r.id)} className="text-[11px] text-[color:var(--st-info)] font-medium" data-testid={`sh-job-${r.id}-runs`}>Triggers</button> }]}
          rows={data.rows} />
      </Card>
      {runs && (
        <Card title={`Cron triggers · ${runs.job_id}`} right={<button onClick={() => setRuns(null)} className="text-[11px]" data-testid="sh-job-runs-close">Close</button>} testId="sh-job-runs">
          <Table empty="No triggers recorded yet (cron hits are logged automatically from the platform scheduler)" rowKey={(r) => r.id}
            cols={[{ k: "at", h: "When", r: (r) => new Date(r.at).toLocaleString() }, { k: "status", h: "HTTP" }, { k: "ms", h: "ms" }, { k: "path", h: "Path", r: (r) => <span className="font-mono text-[11px]">{r.path}</span> }]}
            rows={runs.rows} />
        </Card>
      )}
    </div>
  );
}

export function IntegrationsTab() {
  const { data, err } = useFetch("/system-health/integrations");
  if (err) return <div className="text-[12px] text-[color:var(--st-critical)]">{err}</div>;
  if (!data) return <div className="os-skeleton h-40" />;
  return (
    <Card title={`Integrations · ${data.detail}`} testId="sh-tab-integrations">
      <Table testId="sh-integrations-table" empty="No integrations registered" rowKey={(r) => r.key}
        cols={[{ k: "status", h: "Status", r: (r) => <StatusPill status={r.status} testId={`sh-int-${r.key}-status`} /> },
          { k: "label", h: "Integration", r: (r) => <div><b>{r.label}</b><div className="text-[10px] font-mono text-[color:var(--st-text-muted)]">{r.raw_status} · auth {r.auth_status}{r.note ? ` · ${r.note}` : ""}</div></div> },
          { k: "last_success", h: "Last success", r: (r) => fmtSince(r.last_success) },
          { k: "last_failure", h: "Last failure", r: (r) => fmtSince(r.last_failure) },
          { k: "api_errors_7d", h: "Errors 7d" },
          { k: "last_error", h: "Last error", r: (r) => <span className="text-[11px] break-all">{r.last_error || "—"}{r.credential_expiring ? " · credential expiring" : ""}</span> }]}
        rows={data.rows} />
    </Card>
  );
}

export function LogsTab({ isAdmin }) {
  const [level, setLevel] = useState("ALL");
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  useEffect(() => { const t = setTimeout(() => setDq(q), 300); return () => clearTimeout(t); }, [q]);
  const [lines, setLines] = useState(200);
  const { data, err, busy, reload } = useFetch(`/system-health/logs?lines=${lines}&level=${level}&q=${encodeURIComponent(dq)}`, [level, lines, dq]);
  if (!isAdmin) return <div className="os-card p-6 text-[12px] text-[color:var(--st-text-muted)]" data-testid="sh-logs-forbidden">Raw application logs are visible to Super Admin / Admin / Management only.</div>;
  if (err) return <div className="text-[12px] text-[color:var(--st-critical)]" data-testid="sh-logs-error">{err}</div>;
  const tone = { ERROR: "text-[color:var(--st-critical)]", CRITICAL: "text-[color:var(--st-critical)]", WARNING: "text-[color:var(--st-warn)]", INFO: "text-[color:var(--st-info)]" };
  return (
    <div className="space-y-3" data-testid="sh-tab-logs">
      <div className="flex flex-wrap items-center gap-2 text-[12px]">
        {["ALL", "WARNING", "ERROR"].map((l) => <button key={l} onClick={() => setLevel(l)} data-testid={`sh-logs-level-${l}`} className={`h-7 px-3 rounded-full border text-[11px] font-medium ${level === l ? "bg-[color:var(--st-text)] text-white border-transparent" : "border-[color:var(--st-border)] bg-white"}`}>{l}</button>)}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search message / logger…" className="h-7 px-2 rounded-md border border-[color:var(--st-border)] text-[12px] w-56" data-testid="sh-logs-search" />
        <select value={lines} onChange={(e) => setLines(Number(e.target.value))} className="h-7 px-2 rounded-md border border-[color:var(--st-border)] text-[12px]" data-testid="sh-logs-lines">{[100, 200, 500, 1000].map((n) => <option key={n} value={n}>{n} lines</option>)}</select>
        {data?.summary && <span className="text-[11px] text-[color:var(--st-text-muted)]" data-testid="sh-logs-summary">{data.summary.errors_24h} errors · {data.summary.warnings_24h} warnings · {data.summary.tracebacks_24h} tracebacks (24 h)</span>}
        <button onClick={reload} className="ml-auto h-7 w-7 grid place-items-center rounded-md hover:bg-[color:var(--st-surface-alt)]" data-testid="sh-logs-refresh"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /></button>
      </div>
      <div className="rounded-xl bg-[#0B1730] text-[#E6EDF7] p-3 font-mono text-[11px] leading-relaxed max-h-[560px] overflow-auto" data-testid="sh-logs-viewer">
        {!data ? <div className="opacity-60">Loading…</div> : data.rows.length === 0 ? <div className="opacity-60">No log lines match.</div> : data.rows.map((r, i) => (
          <div key={i} className="whitespace-pre-wrap break-words border-b border-white/5 py-0.5">
            <span className="opacity-50">{r.at || "—"}</span> <span className={`font-semibold ${tone[r.level] || "opacity-70"}`}>{r.level}</span> <span className="opacity-70">{r.logger}</span> — {r.message}
          </div>))}
      </div>
    </div>
  );
}

export function HistoryTab() {
  const { data, err } = useFetch("/system-health/history");
  if (err) return <div className="text-[12px] text-[color:var(--st-critical)]">{err}</div>;
  if (!data) return <div className="os-skeleton h-40" />;
  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-4" data-testid="sh-tab-history">
      <Card title="Availability · 24 h">
        <div className="text-4xl font-semibold tracking-tight" data-testid="sh-uptime-24h">{data.uptime_24h_pct != null ? `${data.uptime_24h_pct}%` : "—"}</div>
        <div className="text-[11px] text-[color:var(--st-text-muted)]">Share of health checks where core systems were operational or only warning.</div>
        <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-[color:var(--st-text-subtle)]">Incidents</div>
        {data.incidents.length === 0 ? <div className="text-[12px] text-[color:var(--st-text-muted)]">No degraded / failed transitions recorded.</div> : data.incidents.map((r) => <div key={r.id} className="text-[12px] flex items-center gap-2"><StatusPill status={r.overall} />{new Date(r.at).toLocaleString()}</div>)}
      </Card>
      <Card title="Status snapshots">
        <Table testId="sh-history-table" empty="No snapshots yet — they are recorded each time the dashboard is checked" rowKey={(r) => r.id}
          cols={[{ k: "at", h: "When", r: (r) => new Date(r.at).toLocaleString() },
            { k: "overall", h: "Overall", r: (r) => <StatusPill status={r.overall} /> },
            { k: "components", h: "Components", r: (r) => <span className="font-mono text-[10px]">{Object.entries(r.components || {}).map(([k, v]) => `${k}:${v[0].toUpperCase()}`).join(" · ")}</span> },
            { k: "changed", h: "", r: (r) => r.changed ? <span className="text-[10px] uppercase font-semibold text-[color:var(--st-warn)]">state change</span> : null }]}
          rows={data.rows} />
      </Card>
    </div>
  );
}
